import {useEffect, useCallback, useRef} from 'react'
import {useBlocker} from 'react-router'

type UseNavigationBlockerReturn = {
  isBlocked: boolean
  proceedNavigation: (skipCallback?: boolean) => void
  cancelNavigation: () => void
}

/**
 * Hook to block and control in-app navigation based on a blocking condition.
 *
 * Uses React Router's native useBlocker hook which is supported with
 * createBrowserRouter + RouterProvider setup (React Router v7+).
 *
 * Use cases include:
 * - Protecting unsaved form changes
 * - Preventing navigation during active uploads
 * - Blocking exit from incomplete wizard flows
 * - Any scenario requiring user confirmation before leaving
 *
 * Intercepts navigation via:
 * - React Router's built-in navigation blocking (links, navigate(), back/forward)
 * - beforeunload (tab close/refresh)
 *
 * @param shouldBlock - Condition that determines if navigation should be blocked
 * @param enabled - Whether the navigation blocking is active (default: true)
 * @param onNavigate - Optional callback to execute before navigation proceeds
 * @returns Object with isBlocked state and navigation control functions
 */
export const useNavigationBlocker = (
  shouldBlock: boolean,
  enabled: boolean = true,
  onNavigate?: () => void
): UseNavigationBlockerReturn => {
  const onNavigateRef = useRef(onNavigate)

  useEffect(() => {
    onNavigateRef.current = onNavigate
  }, [onNavigate])

  const blocker = useBlocker(enabled && shouldBlock)

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!enabled || !shouldBlock) return
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [enabled, shouldBlock])

  const proceedNavigation = useCallback(
    (skipCallback?: boolean) => {
      if (blocker.state !== 'blocked') return

      if (!skipCallback) {
        onNavigateRef.current?.()
      }
      blocker.proceed()
    },
    [blocker]
  )

  const cancelNavigation = useCallback(() => {
    if (blocker.state !== 'blocked') return
    blocker.reset()
  }, [blocker])

  return {
    isBlocked: blocker.state === 'blocked',
    proceedNavigation,
    cancelNavigation
  }
}
